import React from 'react';
import { TriangleAlert } from 'lucide-react';
import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  heading?: string;
  description: React.ReactNode;
  iconTone?: 'amber' | 'red';
  cancelLabel?: string;
  confirmLabel: string;
  confirmingLabel?: string;
  submitting?: boolean;
  errorMessage?: string | null;
  onConfirm: () => void | Promise<void>;
  testId?: string;
  cancelTestId?: string;
  confirmTestId?: string;
  errorTestId?: string;
}

export const WorkOrderConfirmModal: React.FC<Props> = ({
  isOpen,
  onClose,
  title,
  heading,
  description,
  iconTone = 'amber',
  cancelLabel,
  confirmLabel,
  confirmingLabel,
  submitting = false,
  errorMessage,
  onConfirm,
  testId,
  cancelTestId,
  confirmTestId,
  errorTestId,
}) => {
  const { t } = useLanguage();
  const resolvedHeading = heading ?? t('workOrders.common.areYouSure');
  const resolvedCancel = cancelLabel ?? t('common.actions.back', undefined, 'Back');
  const iconBgClass = iconTone === 'red' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="md">
      <div className="space-y-4" data-test-id={testId}>
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 flex h-10 w-10 items-center justify-center rounded-full ${iconBgClass}`}>
            <TriangleAlert className="h-5 w-5" />
          </div>
          <div className="space-y-1">
            <div className="text-sm font-semibold text-gray-900">{resolvedHeading}</div>
            <div className="text-sm text-gray-600">{description}</div>
          </div>
        </div>
        {errorMessage && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" data-test-id={errorTestId}>
            {errorMessage}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            data-test-id={cancelTestId}
          >
            {resolvedCancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            data-test-id={confirmTestId}
          >
            {submitting && confirmingLabel ? confirmingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};
